import { Link } from 'react-router-dom'
import Navigation from './Navigation'
import { Mail, Phone, MapPin } from 'lucide-react'
import { motion } from 'framer-motion'

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-background">
      {/* Top Bar */}
      <div className="bg-primary text-primary-foreground py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <a href="tel:01273326200" className="flex items-center gap-1 hover:opacity-80">
                <Phone size={14} />
                <span>01273 326200</span>
              </a>
              <a href="mailto:info@hill-bros.co.uk" className="flex items-center gap-1 hover:opacity-80">
                <Mail size={14} />
                <span>info@hill-bros.co.uk</span>
              </a>
            </div>
            <div className="flex items-center gap-1">
              <MapPin size={14} />
              <span>Serving Brighton & Hove for Over 45 Years</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="shadow-md sticky top-0 z-50" style={{ backgroundColor: 'rgb(230, 49, 30)' }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3 md:py-4">
            {/* Logo section */}
            <Link to="/" className="flex items-center gap-2 md:gap-4">
              <motion.img 
                src="/logo.JPG" 
                alt="Hill Bros Logo" 
                className="h-12 sm:h-14 md:h-20"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
              <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="hidden sm:inline">HILL BROS BRIGHTON LTD</span>
                <span className="sm:hidden">HILL BROS</span>
              </h1>
            </Link>

            {/* CTA Button - Hidden on mobile, shown on desktop */}
            <motion.a 
              href="mailto:info@hill-bros.co.uk"
              className="hidden lg:block bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Free Quote
            </motion.a>
          </div>
        </div>

        {/* Navigation */}
        <Navigation />
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Hill Bros</h3>
              <p className="text-gray-400">
                Family-run roofing and building contractors serving Brighton & Hove 
                with pride for over 45 years.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li><Link to="/about" className="hover:text-primary transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-primary transition-colors">Services</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/services/roofing" className="hover:text-primary transition-colors">Roofing</Link></li>
                <li><Link to="/services/flat-roofing" className="hover:text-primary transition-colors">Flat Roofing</Link></li>
                <li><Link to="/services/general-building" className="hover:text-primary transition-colors">General Building</Link></li>
                <li><Link to="/services/damp-proofing" className="hover:text-primary transition-colors">Damp Proofing</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Contact Info</h3>
              <div className="space-y-3 text-gray-400">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="flex items-start gap-2 mb-1">
                      <MapPin size={14} className="mt-0.5" />
                      <span className="font-semibold">Contact Address</span>
                    </div>
                    <div>31 Connaught Road<br />Seaford<br />BN25 2PU</div>
                  </div>
                  <div>
                    <div className="flex items-start gap-2 mb-1">
                      <MapPin size={14} className="mt-0.5" />
                      <span className="font-semibold">Yard Address</span>
                    </div>
                    <div>Maritime House<br />Basin Road North<br />Brighton and Hove<br />Brighton<br />BN41 1WR</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <a href="mailto:info@hill-bros.co.uk" className="hover:text-primary transition-colors">
                    info@hill-bros.co.uk
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-gray-400">
              <p>&copy; {new Date().getFullYear()} Hill Bros Brighton Ltd. All rights reserved.</p>
              <div className="flex gap-4 mt-4 md:mt-0 text-sm">
                <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
                <span>|</span>
                <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
                <span>|</span>
                <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout